import { TFunction } from 'i18next';
import {
  CommandParameterDescriptor,
  CommandParameterOption,
  CommandState,
  CommandType,
} from '@thingslog/repositories';

export const commandTypeKey = (commandType: string): string =>
  `commands_command_type_${commandType.toLowerCase()}`;

export const commandStateKey = (commandState: string): string =>
  `commands_command_state_${
    commandState === 'CANCELED' ? 'cancelled' : commandState.toLowerCase()
  }`;

export const translateCommandType = (t: TFunction, commandType: CommandType | string): string =>
  t(commandTypeKey(commandType), { defaultValue: commandType });

export const translateCommandState = (t: TFunction, commandState: CommandState | string): string =>
  t(commandStateKey(commandState), { defaultValue: commandState });

export interface ParsedParameterName {
  baseName: string;
  groupIndex: number | null;
}

const GROUP_SUFFIX = /^(.*)_(\d+)$/;

export const parseParameterName = (parameterName: string): ParsedParameterName => {
  const match = GROUP_SUFFIX.exec(parameterName);

  // `relay_1_status` is not grouped: the suffix must be the final segment.
  if (!match) {
    return { baseName: parameterName, groupIndex: null };
  }

  return { baseName: match[1], groupIndex: Number(match[2]) };
};

export const translateParameterName = (t: TFunction, parameterName: string): string => {
  const { baseName } = parseParameterName(parameterName);
  return t(`commands_${baseName}`, { defaultValue: humanizeParameterName(baseName) });
};

export const humanizeParameterName = (parameterName: string): string =>
  parameterName
    .split('_')
    .filter((part: string): boolean => part.length > 0)
    .map((part: string): string => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

/** Commands store only raw values; descriptors carry the display names. */
export type ParameterValueLabels = Map<string, string>;

export const parameterValueKey = (
  commandType: string,
  parameterName: string,
  value: string
): string => `${commandType}:${parameterName}:${value}`;

export const collectParameterValueLabels = (
  commandType: CommandType,
  descriptors: CommandParameterDescriptor[]
): ParameterValueLabels => {
  const labels: ParameterValueLabels = new Map<string, string>();

  descriptors.forEach((descriptor: CommandParameterDescriptor): void => {
    if (descriptor['@type'] !== 'enum') return;

    descriptor.values.forEach((option: CommandParameterOption): void => {
      labels.set(
        parameterValueKey(commandType, descriptor.parameterName, option.value),
        option.displayName
      );
    });
  });

  return labels;
};

/** Only mappings every describing type agrees on; RELAY_STATUSES describes none. */
export type SharedParameterValueLabels = Map<string, string | null>;

export const sharedParameterValueKey = (parameterName: string, value: string): string =>
  `${parameterName}:${value}`;

export const collectSharedParameterValueLabels = (
  descriptorsByType: [CommandType, CommandParameterDescriptor[]][]
): SharedParameterValueLabels => {
  const shared: SharedParameterValueLabels = new Map<string, string | null>();

  descriptorsByType.forEach(
    ([, descriptors]: [CommandType, CommandParameterDescriptor[]]): void => {
      descriptors.forEach((descriptor: CommandParameterDescriptor): void => {
        if (descriptor['@type'] !== 'enum') return;

        descriptor.values.forEach((option: CommandParameterOption): void => {
          const key = sharedParameterValueKey(descriptor.parameterName, option.value);
          if (!shared.has(key)) {
            shared.set(key, option.displayName);
          } else if (shared.get(key) !== option.displayName) {
            shared.set(key, null);
          }
        });
      });
    }
  );

  return shared;
};

export const translateParameterValue = (
  labels: ParameterValueLabels,
  commandType: string,
  parameterName: string,
  value: string,
  shared?: SharedParameterValueLabels
): string => {
  const exact = labels.get(parameterValueKey(commandType, parameterName, value));
  if (exact !== undefined) return exact;

  return shared?.get(sharedParameterValueKey(parameterName, value)) ?? value;
};

// LocalDateTime arrives zoneless; appending a Z would shift every timestamp.
export const parseCommandDate = (value: string | null): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};
